import { reactive } from "vue";
import type { Product } from "@/services/product";
import type { Coupon } from "@/services/coupon";
import type { CustomerDisplayPoster } from "@/services/setting";
import { DEFAULT_CUSTOMER_DISPLAY_POSTERS } from "@/utils/customerDisplayPosters";
import { normalizeDiscountType } from "@/utils/posPricing";
import type { ActivePromotion } from "@/utils/promotion";

interface StateStore {
  open: boolean;
  onChange: () => void;
  selectedWarehouseId: number | null;
  selectedWarehouseName: string;
  setWarehouse: (id: number | null, name: string) => void;
  selectedProducts: Array<{ 
    id: number; 
    name: string; 
    code: string; 
    qty: number; 
    stock: number; 
    price: number; 
    discount: number;
    discount_type: 'fixed' | 'percentage';
    size_id?: number | null; 
    sugar_id?: number | null;
    product_sizes?: any[];
    type?: number;
  }>;
  printType: "barcode" | "qrcode";
  showName: boolean;
  showCode: boolean;
  showPrice: boolean;
  refreshTrigger: number;
  triggerRefresh: () => void;
  addSelectedProduct: (p: Product) => void;
  removeSelectedProduct: (id: number) => void;
  setProductQty: (id: number, qty: number) => void;
  setProductDiscount: (id: number, discount: number, type: 'fixed' | 'percentage') => void;
  setProductVariation: (id: number, type: 'size' | 'sugar', val: number | null) => void;
  setPrintType: (type: "barcode" | "qrcode") => void;
  clearSelectedProducts: () => void;
  orderDiscount: number;
  orderDiscountType: 'fixed' | 'percentage';
  orderTax: number;
  orderTaxType: 'fixed' | 'percentage';
  shippingCost: number;
  selectedCustomerId: number | null;
  referenceNo: string;
  coupon: Coupon | null;
  activePromotion: ActivePromotion | null;
  customerDisplayPosters: CustomerDisplayPoster[];
  setOrderDiscount: (discount: number, type: 'fixed' | 'percentage') => void;
  setOrderTax: (tax: number, type: 'fixed' | 'percentage') => void;
  setShippingCost: (cost: number) => void;
  setCoupon: (coupon: Coupon | null) => void;
  setActivePromotion: (promotion: ActivePromotion | null) => void;
  setCustomerDisplayPosters: (posters: CustomerDisplayPoster[]) => void;
  payments: Array<{ method: string; amount: number; reference?: string }>;
  addPayment: (payment: { method: string; amount: number; reference?: string }) => void;
  removePayment: (index: number) => void;
  clearPayments: () => void;
  registerStatus: {
    isOpen: boolean;
    id: number | null;
    openingBalance: number;
  };
  setRegisterStatus: (isOpen: boolean, id: number | null, balance?: number) => void;
  currencySymbol: string;
  setCurrencySymbol: (symbol: string) => void;
  formatPrice: (price: number) => string;
  logo: string;
  setLogo: (logo: string) => void;
  siteName: string;
  setSiteName: (name: string) => void;
  companyPhone: string;
  companyEmail: string;
  companyAddress: string;
  setCompanyInfo: (info: {
    phone?: string;
    email?: string;
    address?: string;
  }) => void;
  receiptSettings: {
    showPhone: boolean;
    showAddress: boolean;
    showEmail: boolean;
    showCustomer: boolean;
    showTaxDiscount: boolean;
    showBarcode: boolean;
    showNoteToCustomer: boolean;
    noteToCustomer: string;
  };
  setReceiptSettings: (settings: {
    showPhone?: boolean;
    showAddress?: boolean;
    showEmail?: boolean;
    showCustomer?: boolean;
    showTaxDiscount?: boolean;
    showBarcode?: boolean;
    showNoteToCustomer?: boolean;
    noteToCustomer?: string;
  }) => void;
  referencePrefixes: {
    products: string;
    supplier: string;
    purchase: string;
    sales: string;
    pos: string;
    customer: string;
    expense: string;
    income: string;
    stockTransfer: string;
    stockAdjustment: string;
    estimation: string;
    transaction: string;
    employee: string;
  };
  setReferencePrefixes: (prefixes: Partial<StateStore["referencePrefixes"]>) => void;
}

const STORAGE_KEY = "pos_selected_products";

const loadSavedProducts = (): any[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    console.error("Failed to load saved products", e);
    return [];
  }
};

const saveProducts = (products: any[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
};

import Swal from 'sweetalert2';

// Helper function for professional alerts
const showStockAlert = (message: string, detail = '') => {
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'warning',
    title: message,
    text: detail,
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    customClass: {
      popup: 'colored-toast'
    }
  });
};

const getProductStockForWarehouse = (p: Product, warehouseId: number | null): number => {
  if (Number(p.type) !== 1) {
    return Number(p.qty) || 0;
  }

  const stocks = p.stocks || [];
  if (!stocks.length) {
    return 0;
  }

  if (warehouseId) {
    const matchingStocks = stocks.filter(
      (stock) => Number(stock.warehouse_id) === Number(warehouseId)
    );

    if (!matchingStocks.length) {
      return 0;
    }

    return matchingStocks.reduce((total, stock) => {
      const value = stock.qty ?? stock.quantity ?? 0;
      return total + (Number(value) || 0);
    }, 0);
  }

  return stocks.reduce((total, stock) => {
    const value = stock.qty ?? stock.quantity ?? 0;
    return total + (Number(value) || 0);
  }, 0);
};

const normalizeProductSizes = (productSizes: any[] | undefined | null) =>
  Array.isArray(productSizes)
    ? productSizes.map((productSize) => ({
        ...productSize,
        size_id:
          productSize?.size_id === null || productSize?.size_id === undefined
            ? null
            : Number(productSize.size_id),
        price: Number(productSize?.price || 0),
      }))
    : [];

const findProductSizePrice = (
  productSizes: any[] | undefined | null,
  sizeId: number | null
): number | null => {
  if (!Array.isArray(productSizes) || sizeId === null) {
    return null;
  }

  const matchedSize = productSizes.find(
    (productSize) => Number(productSize?.size_id) === Number(sizeId)
  );

  if (!matchedSize) {
    return null;
  }

  return Number(matchedSize.price || 0);
};

const stateStore: StateStore = reactive({
  open: false,
  onChange ()  {
    stateStore.open = !stateStore.open
  },
  selectedWarehouseId: null,
  selectedWarehouseName: "",
  setWarehouse(id, name) {
    stateStore.selectedWarehouseId = id;
    stateStore.selectedWarehouseName = name || "";
  },
  selectedProducts: loadSavedProducts(),
  printType: "barcode",
  showName: true,
  showCode: true,
  showPrice: true,
  refreshTrigger: 0,
  triggerRefresh() {
    stateStore.refreshTrigger++;
  },
  addSelectedProduct(p) {
    const id = p.id!;
    const exists = stateStore.selectedProducts.find(sp => sp.id === id);

    const stock = getProductStockForWarehouse(p, stateStore.selectedWarehouseId);

    // 2. Check if product is already in cart
    if (exists) {
      if (exists.qty >= stock && p.type === 1) {
        showStockAlert(
          "Stock Limit Reached!",
          `You already have ${exists.qty} in cart. Only ${stock} available.`
        );
        return;
      }
      exists.qty += 1;
      saveProducts(stateStore.selectedProducts);
      return;
    }

    // 3. Check initial stock for new item
    if (stock <= 0 && p.type === 1) {
      const warehouseMsg = stateStore.selectedWarehouseName ? ` in ${stateStore.selectedWarehouseName}` : '';
      showStockAlert(
        "Out of Stock!",
        `This product is not available${warehouseMsg}.`
      );
      return;
    }

    const productSizes = normalizeProductSizes(p.product_sizes);

    stateStore.selectedProducts.push({
      id,
      name: p.name,
      code: p.code,
      qty: 1,
      stock: stock,
      price: p.price || 0,
      discount: Math.max(0, Number(p.discount || 0)),
      discount_type: normalizeDiscountType(p.discount_type),
      size_id: p.size_id || null,
      sugar_id: p.sugar_id || null,
      product_sizes: productSizes,
      type: p.type,
    });
    saveProducts(stateStore.selectedProducts);
  },
  removeSelectedProduct(id) {
    stateStore.selectedProducts = stateStore.selectedProducts.filter(sp => sp.id !== id);
    saveProducts(stateStore.selectedProducts);
  },
  setProductQty(id, qty) {
    const sp = stateStore.selectedProducts.find(x => x.id === id);
    if (sp) {
      if (sp.type === 1 && qty > sp.stock && sp.stock !== undefined) {
        showStockAlert(
          "Stock Limit Exceeded!",
          `Cannot exceed available stock (${sp.stock}).`
        );
        sp.qty = sp.stock;
      } else {
        sp.qty = Math.max(1, Math.floor(qty));
      }
      saveProducts(stateStore.selectedProducts);
    }
  },
  setProductDiscount(id, discount, type) {
    const sp = stateStore.selectedProducts.find(x => x.id === id);
    if (sp) {
      sp.discount = Math.max(0, discount);
      sp.discount_type = type;
      saveProducts(stateStore.selectedProducts);
    }
  },
  setProductVariation(id, type, val) {
    const sp = stateStore.selectedProducts.find(x => x.id === id);
    if (sp) {
      if (type === 'size') {
        sp.size_id = val;
        // Update price based on size if product_sizes exist
        const sizePrice = findProductSizePrice(sp.product_sizes, val);
        if (sizePrice !== null) {
          sp.price = sizePrice;
        }
      }
      if (type === 'sugar') sp.sugar_id = val;
      saveProducts(stateStore.selectedProducts);
    }
  },
  setPrintType(type) {
    stateStore.printType = type;
  },
  clearSelectedProducts() {
    stateStore.selectedProducts = [];
    stateStore.orderDiscount = 0;
    stateStore.orderDiscountType = 'fixed';
    stateStore.orderTax = 0;
    stateStore.orderTaxType = 'fixed';
    stateStore.shippingCost = 0;
    stateStore.coupon = null;
    stateStore.activePromotion = null;
    localStorage.removeItem(STORAGE_KEY);
  },
  orderDiscount: 0,
  orderDiscountType: 'fixed',
  orderTax: 0,
  orderTaxType: 'fixed',
  shippingCost: 0,
  selectedCustomerId: null,
  referenceNo: "",
  coupon: null,
  activePromotion: null,
  customerDisplayPosters: [...DEFAULT_CUSTOMER_DISPLAY_POSTERS],
  setOrderDiscount(discount, type) {
    stateStore.orderDiscount = Math.max(0, discount);
    stateStore.orderDiscountType = type;
  },
  setOrderTax(tax, type) {
    stateStore.orderTax = Math.max(0, tax);
    stateStore.orderTaxType = type;
  },
  setShippingCost(cost) {
    stateStore.shippingCost = Math.max(0, cost);
  },
  setCoupon(coupon) {
    stateStore.coupon = coupon;
  },
  setActivePromotion(promotion) {
    stateStore.activePromotion = promotion;
  },
  setCustomerDisplayPosters(posters) {
    stateStore.customerDisplayPosters = Array.isArray(posters)
      ? posters.map((poster) => ({ ...poster }))
      : [...DEFAULT_CUSTOMER_DISPLAY_POSTERS];
  },
  payments: [],
  addPayment(payment) {
    stateStore.payments.push(payment);
  },
  removePayment(index) {
    stateStore.payments.splice(index, 1);
  },
  clearPayments() {
    stateStore.payments = [];
  },
  registerStatus: {
    isOpen: false,
    id: null,
    openingBalance: 0,
  },
  setRegisterStatus(isOpen, id, balance = 0) {
    stateStore.registerStatus.isOpen = isOpen;
    stateStore.registerStatus.id = id;
    stateStore.registerStatus.openingBalance = balance;
  },
  currencySymbol: "$",
  setCurrencySymbol(symbol) {
    stateStore.currencySymbol = symbol;
  },
  formatPrice(price) {
    const formattedPrice = Number(price || 0).toFixed(2);
    return stateStore.currencySymbol === "៛" 
      ? `${formattedPrice} ${stateStore.currencySymbol}` 
      : `${stateStore.currencySymbol}${formattedPrice}`;
  },
  logo: "",
  setLogo(logo) {
    stateStore.logo = logo;
  },
  siteName: "ProCare Solution",
  setSiteName(name) {
    stateStore.siteName = name;
  },
  companyPhone: "",
  companyEmail: "",
  companyAddress: "",
  setCompanyInfo(info) {
    stateStore.companyPhone = info.phone || "";
    stateStore.companyEmail = info.email || "";
    stateStore.companyAddress = info.address || "";
  },
  receiptSettings: {
    showPhone: true,
    showAddress: true,
    showEmail: false,
    showCustomer: true,
    showTaxDiscount: true,
    showBarcode: false,
    showNoteToCustomer: true,
    noteToCustomer: "Thanks for shopping with us. Please come again.",
  },
  setReceiptSettings(settings) {
    stateStore.receiptSettings = {
      ...stateStore.receiptSettings,
      ...settings,
      noteToCustomer:
        settings.noteToCustomer ?? stateStore.receiptSettings.noteToCustomer,
    };
  },
  referencePrefixes: {
    products: "SKU-",
    supplier: "SUP-",
    purchase: "PUR-",
    sales: "SALE-",
    pos: "PINV",
    customer: "CT-",
    expense: "EX-",
    income: "IN-",
    stockTransfer: "ST-",
    stockAdjustment: "SA-",
    estimation: "EST-",
    transaction: "TRN-",
    employee: "EMP-",
  },
  setReferencePrefixes(prefixes) {
    stateStore.referencePrefixes = {
      ...stateStore.referencePrefixes,
      ...prefixes,
    };
  },
})

export default stateStore
